import createReducer from '../../utils/createReducer';
import { getBrandId } from '../../config';
import { trackVisitor } from '../../services/pageViewService';
import {
  arePromoParamsDifferent,
  getUserIP,
  buildVisitorRequestBody,
  parseOldBtagParams,
} from '../../utils/pageViewService';

const validAffiliateFields = [
  'referrerUserId',
  'promoCode',
  'casinoBrandId',
  'utmSource',
  'utmCampaign',
  'utmMedium',
  'utmContent',
  'utmTerm',
  'pid',
  'clickid',
  'httpReferer',
  'ip',
];

const reduceValidFields = (fields) =>
  Object.keys(fields).reduce((result, name) => {
    if (validAffiliateFields.indexOf(name) > -1) {
      return { ...result, [name]: fields[name] };
    }

    return result;
  }, {});

const KEY = 'affiliate';
const SET_FIELDS = `${KEY}/set-affiliate-fields`;
const CLEAR = `${KEY}/clear`;

function setAffiliateFields(sourceFields, shouldClearPrevious = false) {
  return async (dispatch, getState) => {
    const { affiliate: currentAffiliate } = getState();
    const fields = { ...sourceFields };

    const paramsDifferent = arePromoParamsDifferent(currentAffiliate, fields);

    if (fields.promoCode) {
      const oldBtagParams = parseOldBtagParams(currentAffiliate.btag);
      const stored = { ...oldBtagParams, ...currentAffiliate };

      const hasOldFormatData = currentAffiliate.referrerUserId && !currentAffiliate.promoCode;
      const referrerUserIdToSend = paramsDifferent && !hasOldFormatData ? null : currentAffiliate.referrerUserId;
      const ip = await getUserIP();

      const requestBody = buildVisitorRequestBody({
        referrerUserId: referrerUserIdToSend,
        promoCode: fields.promoCode,
        casinoBrandId: getBrandId(),
        utmSource: fields.utmSource || stored.utmSource || null,
        utmCampaign: fields.utmCampaign || stored.utmCampaign || null,
        utmMedium: fields.utmMedium || stored.utmMedium || null,
        utmContent: fields.utmContent || stored.utmContent || null,
        utmTerm: fields.utmTerm || stored.utmTerm || null,
        pid: fields.pid || stored.pid || null,
        clickid: fields.clickid || stored.clickid || null,
        ip,
        httpReferer: fields.httpReferer || stored.httpReferer || null,
      });

      const response = await trackVisitor(requestBody);

      if (response?.referrerUserId) {
        fields.referrerUserId = response.referrerUserId;
      }

      if (ip) {
        fields.ip = ip;
      }
    }

    dispatch({
      type: SET_FIELDS,
      payload: {
        fields,
        shouldClearPrevious: shouldClearPrevious || paramsDifferent,
      },
    });
  };
}

function sendVisitorTracking() {
  return async (dispatch, getState) => {
    const affiliate = getState().affiliate;

    if (!affiliate || (!affiliate.promoCode && !affiliate.referrerUserId)) {
      return;
    }

    const oldBtagParams = parseOldBtagParams(affiliate.btag);

    const referrerUserId = affiliate.referrerUserId;
    const promoCode = affiliate.promoCode || (oldBtagParams && oldBtagParams.promoCode) || null;
    const utmSource = affiliate.utmSource || (oldBtagParams && oldBtagParams.utmSource) || null;
    const utmCampaign = affiliate.utmCampaign || (oldBtagParams && oldBtagParams.utmCampaign) || null;
    const utmMedium = affiliate.utmMedium || (oldBtagParams && oldBtagParams.utmMedium) || null;
    const utmContent = affiliate.utmContent || (oldBtagParams && oldBtagParams.utmContent) || null;
    const utmTerm = affiliate.utmTerm || (oldBtagParams && oldBtagParams.utmTerm) || null;
    const pid = affiliate.pid || (oldBtagParams && oldBtagParams.pid) || null;
    const clickid = affiliate.clickid || (oldBtagParams && oldBtagParams.clickid) || null;
    const httpReferer = affiliate.httpReferer || (oldBtagParams && oldBtagParams.httpReferer) || null;
    const ip = affiliate.ip || null;

    const requestBody = buildVisitorRequestBody({
      referrerUserId: referrerUserId || null,
      promoCode,
      casinoBrandId: getBrandId(),
      utmSource,
      utmCampaign,
      utmMedium,
      utmContent,
      utmTerm,
      pid,
      clickid,
      ip,
      httpReferer,
    });

    const response = await trackVisitor(requestBody);

    if (response?.referrerUserId && response.referrerUserId !== referrerUserId) {
      dispatch({
        type: SET_FIELDS,
        payload: {
          fields: {
            referrerUserId: response.referrerUserId,
            promoCode,
            casinoBrandId: getBrandId(),
            utmSource,
            utmCampaign,
            utmMedium,
            utmContent,
            utmTerm,
            pid,
            clickid,
            ip,
            httpReferer,
          },
          shouldClearPrevious: true,
        },
      });
    }
  };
}

function clear() {
  return {
    type: CLEAR,
  };
}

const initialState = {};

const actionHandlers = {
  [SET_FIELDS]: (state, { payload }) =>
    reduceValidFields({
      ...(payload.shouldClearPrevious ? {} : state),
      ...payload.fields,
    }),
  [CLEAR]: () => ({ ...initialState }),
};

const actionTypes = {
  SET_FIELDS,
  CLEAR,
};

const actionCreators = {
  setAffiliateFields,
  sendVisitorTracking,
  clear,
};

export { initialState, actionTypes, actionCreators, actionHandlers };

export default createReducer(initialState, actionHandlers);
